import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormData } from '../App';
import { RefreshCcw, Shield, Star } from 'lucide-react';
import { saveSubmission } from '../db/client';

interface Props {
  formData: FormData;
  resetForm: () => void;
}

function Results({ formData, resetForm }: Props) {
  const navigate = useNavigate();

  const calculateTotals = () => {
    const herbicideCosts = formData.herbicides.reduce(
      (total, herb) => total + herb.products.reduce((sum, p) => sum + p.cost, 0) + herb.sprayManCost,
      0
    );
    const manualWeedingCosts = formData.manualWeedingDetails.reduce(
      (total, detail) => total + detail.laborers * detail.costPerLaborer,
      0
    );

    const herbicideControlDays = formData.herbicides.reduce(
      (total, herb) => total + herb.controlDuration,
      0
    );
    const manualWeedingControlDays = formData.manualWeedingDetails.reduce(
      (total, detail) => total + detail.controlDuration,
      0
    );

    return {
      herbicideCosts,
      manualWeedingCosts,
      totalCost: herbicideCosts + manualWeedingCosts,
      herbicideControlDays,
      manualWeedingControlDays,
      totalControlDays: herbicideControlDays + manualWeedingControlDays,
    };
  };

  const totals = calculateTotals();

  useEffect(() => {
    const saveData = async () => {
      try {
        await saveSubmission(formData, totals);
      } catch (error) {
        console.error('Error auto-saving submission:', error);
      }
    };
    saveData();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Cost Analysis Results</h2>
        <p className="text-gray-600 mb-6">
          Here's the breakdown of your weed control costs and duration.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="backdrop-blur-md bg-white/30 p-6 rounded-2xl border border-white/20 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">State:</span>
              <span className="font-semibold">{formData.state}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">District:</span>
              <span className="font-semibold">{formData.district}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pattern:</span>
              <span className="font-semibold">{formData.croppingPattern}</span>
            </div>
          </div>
        </div>

        <div className="backdrop-blur-md bg-white/30 p-6 rounded-2xl border border-white/20 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Cost Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Herbicide Application Costs:</span>
              <span className="font-semibold">₹{totals.herbicideCosts}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Manual Weeding Costs:</span>
              <span className="font-semibold">₹{totals.manualWeedingCosts}</span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between items-center text-lg">
                <span className="font-semibold text-gray-800">Total Cost:</span>
                <span className="font-bold text-brand-green">₹{totals.totalCost}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="backdrop-blur-md bg-white/30 p-6 rounded-2xl border border-white/20 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Control Duration</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Herbicide Application Control:</span>
            <span className="font-semibold">{totals.herbicideControlDays} days</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Manual Weeding Control:</span>
            <span className="font-semibold">{totals.manualWeedingControlDays} days</span>
          </div>
          <div className="border-t pt-3">
            <div className="flex justify-between items-center text-lg">
              <span className="font-semibold text-gray-800">Total Days of Control:</span>
              <span className="font-bold text-brand-blue">{totals.totalControlDays} days</span>
            </div>
          </div>
        </div>
      </div>

      <div className="backdrop-blur-md bg-white/30 p-6 rounded-2xl border border-white/20 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Ratings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-600">Crop Safety Rating</label>
            <div className="flex items-center mt-2">
              <Shield className="w-5 h-5 text-brand-blue mr-2" />
              <div className="flex-1 h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-brand-blue rounded-full"
                  style={{ width: `${(formData.cropSafetyRating / 10) * 100}%` }}
                ></div>
              </div>
              <span className="ml-3 font-semibold">{formData.cropSafetyRating}/10</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">Herbicide Efficacy Rating</label>
            <div className="flex items-center mt-2">
              <Star className="w-5 h-5 text-brand-orange mr-2" />
              <div className="flex-1 h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-brand-orange rounded-full"
                  style={{ width: `${(formData.herbicideEfficacyRating / 10) * 100}%` }}
                ></div>
              </div>
              <span className="ml-3 font-semibold">{formData.herbicideEfficacyRating}/10</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={resetForm}
          className="inline-flex items-center px-6 py-2.5 bg-white/30 backdrop-blur-sm border border-white/20 text-gray-700 rounded-full hover:bg-white/40 transition-all duration-200 font-medium"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Calculate Again
        </button>
      </div>
    </div>
  );
}

export default Results;